// DispatchDeck — Equipment Notes Thread (TASK-1141, Kayla K7)
// ============================================================================
// `window.EquipmentNotes` — per-unit status thread ("unit 123 needs fuel",
// "30 hrs till PM") stored in `tenants/{tid}/{trucks|trailers|assets}/{id}/notes`
// subcollections, replacing the overwrite-the-field chatter on the static
// `notes` FIELD. Includes one-tap "create work order from note" escalation so
// the thread never becomes a shadow maintenance queue (DVIR defects + work
// orders in `tenants/{tid}/maintenance` stay the system of record for repairs).
//
// LOADING (personSections.js precedent — window.* delegation, no cross-module
// ?v= imports per CLAUDE.md Pitfall #1): imported ONCE by views/roster.js
// (app.js static graph, so it evaluates before any handler runs).
// maintenance.js reads `window.EquipmentNotes` at call time — never an import.
//
// AUTHZ MODEL (mirrors the TASK-1141 firestore.rules block — rules are the
// enforcement layer, these client gates are the affordance layer):
//   read    — any tenant member or driver-PIN session (canReadTenant). A
//             driver session sees the thread READ-ONLY (v1 defers driver
//             posting until a real assigned-unit rules helper exists).
//   create  — dispatcher+ role OR roster-module member (non-viewUploadOnly),
//             author fields pinned to auth.uid rules-side.
//   update  — NEVER (append-only history; no edit affordance rendered).
//   delete  — tenant admin (owner/admin) only — cleanup path for accidental
//             PII/garbage without letting authors rewrite the thread.
//   escalate— creates a doc in `tenants/{tid}/maintenance`; the EXISTING
//             maintenance create rule (canWriteTenant && !isComplianceOnly)
//             re-verifies authorization server-side — "a note exists" grants
//             nothing. Client-side the button is hidden without maintenance
//             module visibility.

import {
  db,
  collection,
  doc,
  addDoc,
  getDocs,
  deleteDoc,
  Timestamp,
} from './firebase-config.js?v=1868';

(function () {
  'use strict';

  const KIND_COLLECTIONS = { truck: 'trucks', trailer: 'trailers', asset: 'assets' };
  const BODY_MAX = 2000;

  // One thread mounted at a time (view containers are swapped on navigation,
  // and only one asset modal is open per view). Stale async loads are guarded
  // by re-checking kind/id after each await.
  const State = { kind: null, id: null, unitLabel: '', notes: [] };

  function esc(str) {
    return window.escapeHtml ? window.escapeHtml(str) : String(str == null ? '' : str);
  }

  function tenantId() {
    const t = window.AppState?.tenant;
    return t === 'demo-tenant' ? 'demo' : (t || 'demo');
  }

  function isDriverContext() {
    return !!(window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.accessToken);
  }

  // Mirrors the rules create gate: isDispatcherOrHigher(t) ||
  // (hasModule('roster') && !isViewUploadOnly). Advisory only — firestore.rules
  // enforces.
  function canPost() {
    if (isDriverContext()) return false; // v1: driver posting deferred
    if (window.AppState?.isDemo || tenantId() === 'demo') return true;
    if (!window.AppState?.user) return false;
    const ma = window.AppState?.memberAccess;
    if (!ma) return true; // legacy single-login owner — rules still enforce
    if (['owner', 'admin', 'dispatcher'].includes(ma.role)) return true;
    if (!Array.isArray(ma.modules)) return true; // legacy member doc = full access
    return ma.modules.includes('roster') && ma.viewUploadOnly !== true;
  }

  // Escalation affordance: must be able to post (the escalation writes a
  // follow-up thread note) AND have maintenance visibility. The maintenance
  // create RULE re-verifies server-side regardless of what we show here.
  function canEscalate() {
    if (!canPost()) return false;
    const ma = window.AppState?.memberAccess;
    if (!ma) return true; // demo / legacy owner
    if (['owner', 'admin', 'dispatcher'].includes(ma.role)) return true;
    if (!Array.isArray(ma.modules)) return true;
    return ma.modules.includes('maintenance');
  }

  // Delete affordance mirrors the admin-only delete rule.
  function canDelete() {
    if (window.AppState?.isDemo || tenantId() === 'demo') return true;
    const role = window.AppState?.memberAccess?.role || null;
    if (role === 'owner' || role === 'admin') return true;
    // Legacy single-login owner (no members-doc modules model) — rules enforce.
    return !window.AppState?.memberAccess && !!window.AppState?.user && !isDriverContext();
  }

  function notesCol(kind, id) {
    return collection(db, 'tenants', tenantId(), KIND_COLLECTIONS[kind], id, 'notes');
  }

  function noteMs(n) {
    if (n.createdAt?.toMillis) return n.createdAt.toMillis();
    if (n.createdAt instanceof Date) return n.createdAt.getTime();
    return 0;
  }

  function fmtWhen(ts) {
    const d = ts?.toDate ? ts.toDate() : (ts instanceof Date ? ts : null);
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  // --------------------------------------------------------------------------
  // Section skeleton — header + optional read-only legacy-field line +
  // composer (or read-only notice) + feed container. Inline styles mirror the
  // HR employee-notes feed (hr.js _renderEmployeeNotesFeed) so the thread
  // speaks the same visual language everywhere it mounts.
  // --------------------------------------------------------------------------
  function sectionSkeleton(opts) {
    const legacy = (opts.legacyNote || '').trim();
    // The static `notes` FIELD is legacy for chatter (TASK-1141 split-brain
    // mitigation): render it read-only here so nothing is lost, while all new
    // chatter lands in the thread.
    const legacyHtml = legacy
      ? '<div style="padding:6px 10px;background:var(--bg-secondary,#f8fafc);border:1px dashed var(--border-light,#e5e7eb);border-radius:var(--radius-sm,4px);font-size:0.8rem;color:var(--text-secondary);">'
        + '<span style="font-weight:600;color:var(--text-primary);">Legacy note:</span> '
        + '<span style="white-space:pre-wrap;">' + esc(legacy) + '</span>'
      + '</div>'
      : '';
    let composerHtml = '';
    if (canPost()) {
      composerHtml = ''
        + '<div style="display:flex;gap:8px;align-items:flex-start;">'
        +   '<textarea id="equip-notes-composer" rows="2" maxlength="' + BODY_MAX + '" placeholder="Add a status note — e.g. &quot;needs fuel&quot;, &quot;30 hrs till PM&quot;" style="flex:1 1 auto;resize:vertical;font-size:0.9rem;padding:8px;border:1px solid var(--border-light,#e5e7eb);border-radius:var(--radius-sm,4px);font-family:inherit;background:var(--bg-primary,#fff);color:var(--text-primary);"></textarea>'
        +   '<button type="button" id="equip-notes-post-btn" class="btn btn-sm btn-primary" onclick="window.EquipmentNotes.addNote()" style="flex:0 0 auto;padding:8px 14px;">'
        +     '<span class="material-icons" style="font-size:16px;">send</span> Post'
        +   '</button>'
        + '</div>';
    } else if (isDriverContext()) {
      // Mitigation 2 UI copy — driver sessions view the thread read-only.
      composerHtml = '<div style="font-size:0.78rem;color:var(--text-secondary);">Read-only — posting unit notes from the driver app isn’t available yet. Report an issue through your pre-trip or a maintenance request.</div>';
    }
    return ''
      // data-trailguide: contextual-help hook — content entry lives in
      // trailguide.js TRAILGUIDE_CONTENT under this key ("equip-notes-section");
      // with no content entry the hook is a silent no-op (trailguide.js:1310).
      + '<div class="equip-notes-section" data-trailguide="equip-notes-section" style="display:flex;flex-direction:column;gap:8px;margin-top:8px;">'
      +   '<h4 style="display:flex;align-items:center;gap:6px;margin:0;font-size:0.9rem;">'
      +     '<span class="material-icons" style="font-size:18px;">forum</span> Unit Notes'
      +   '</h4>'
      +   legacyHtml
      +   composerHtml
      +   '<div id="equip-notes-feed" style="display:flex;flex-direction:column;gap:6px;">'
      +     '<div style="font-size:0.8rem;color:var(--text-secondary);">Loading notes…</div>'
      +   '</div>'
      + '</div>';
  }

  // mount(containerId, { kind: 'truck'|'trailer'|'asset', id, unitLabel, legacyNote })
  async function mount(containerId, opts) {
    const el = document.getElementById(containerId);
    if (!el || !opts || !KIND_COLLECTIONS[opts.kind] || !opts.id) return;
    State.kind = opts.kind;
    State.id = opts.id;
    State.unitLabel = opts.unitLabel || '';
    State.notes = [];
    el.innerHTML = sectionSkeleton(opts);
    try {
      const snap = await getDocs(notesCol(opts.kind, opts.id));
      // Stale-load guard: another unit was mounted while we awaited.
      if (State.kind !== opts.kind || State.id !== opts.id) return;
      State.notes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      State.notes.sort((a, b) => noteMs(b) - noteMs(a));
      renderFeed();
    } catch (err) {
      console.error('[EquipmentNotes] load failed:', err);
      const feed = document.getElementById('equip-notes-feed');
      if (feed) feed.innerHTML = '<div style="font-size:0.8rem;color:var(--text-secondary);">Could not load notes.</div>';
    }
  }

  // Clear a container (Add-new modals — no unit yet, no thread).
  function unmount(containerId) {
    State.kind = null;
    State.id = null;
    State.notes = [];
    const el = document.getElementById(containerId);
    if (el) el.innerHTML = '';
  }

  function renderFeed() {
    const feed = document.getElementById('equip-notes-feed');
    if (!feed) return;
    if (State.notes.length === 0) {
      feed.innerHTML = '<div style="font-size:0.8rem;color:var(--text-secondary);padding:4px 0;">No notes yet.</div>';
      return;
    }
    const showEscalate = canEscalate();
    const showDelete = canDelete();
    feed.innerHTML = State.notes.map(n => {
      const isEscalation = !!n.workOrderId;
      const icon = isEscalation
        ? '<span class="material-icons" style="font-size:14px;vertical-align:-2px;color:var(--brand-blue,#3B82F6);">build</span> '
        : '';
      // Escalation affordance: hidden without maintenance access (client) and
      // re-verified by the maintenance create rule (server). Escalation rows
      // themselves don't re-escalate.
      const escBtn = (showEscalate && !isEscalation)
        ? '<button type="button" class="btn btn-sm btn-text" onclick="window.EquipmentNotes.escalateNote(\'' + esc(n.id) + '\')" title="Create a maintenance work order from this note" style="padding:2px 6px;color:var(--brand-blue,#3B82F6);background:none;border:none;cursor:pointer;font-size:0.75rem;display:inline-flex;align-items:center;gap:2px;">'
          + '<span class="material-icons" style="font-size:15px;">build</span> Work order'
        + '</button>'
        : '';
      const delBtn = showDelete
        ? '<button type="button" class="btn btn-sm btn-text" onclick="window.EquipmentNotes.deleteNote(\'' + esc(n.id) + '\')" title="Delete note (admin)" style="padding:2px 6px;color:var(--text-secondary);background:none;border:none;cursor:pointer;">'
          + '<span class="material-icons" style="font-size:15px;">delete</span>'
        + '</button>'
        : '';
      return ''
        + '<div class="equip-note-entry" data-note-id="' + esc(n.id) + '" style="padding:8px 10px;background:var(--bg-secondary,#f8fafc);border:1px solid var(--border-light,#e5e7eb);border-radius:var(--radius-md,6px);">'
        +   '<div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;">'
        +     '<div style="white-space:pre-wrap;font-size:0.88rem;line-height:1.45;color:var(--text-primary);flex:1 1 auto;min-width:0;">' + icon + esc(n.body || '') + '</div>'
        +     '<div style="font-size:0.74rem;color:var(--text-secondary);text-align:right;white-space:nowrap;flex:0 0 auto;">' + esc(fmtWhen(n.createdAt)) + '</div>'
        +   '</div>'
        +   '<div style="display:flex;align-items:center;justify-content:space-between;gap:6px;margin-top:5px;padding-top:5px;border-top:1px dashed var(--border-light,#e5e7eb);">'
        +     '<div style="font-size:0.74rem;color:var(--text-secondary);"><strong style="color:var(--text-primary);">' + esc(n.authorName || 'Unknown') + '</strong></div>'
        +     '<div style="display:flex;gap:2px;align-items:center;">' + escBtn + delBtn + '</div>'
        +   '</div>'
        + '</div>';
    }).join('');
  }

  async function addNote() {
    if (!State.kind || !State.id) return;
    const ta = document.getElementById('equip-notes-composer');
    const btn = document.getElementById('equip-notes-post-btn');
    const body = (ta?.value || '').trim().slice(0, BODY_MAX);
    if (!body) { window.showToast?.('Type a note first', 'info'); ta?.focus(); return; }
    const uid = window.AppState?.user?.uid || null;
    if (!uid) { window.showToast?.('Sign in to add notes', 'error'); return; }
    const authorName = window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown';
    const noteDoc = { body, createdAt: Timestamp.now(), authorUid: uid, authorName };
    try {
      if (btn) btn.disabled = true;
      const newRef = await addDoc(notesCol(State.kind, State.id), noteDoc);
      State.notes.unshift({ id: newRef.id, ...noteDoc });
      renderFeed();
      if (ta) ta.value = '';
    } catch (err) {
      console.error('[EquipmentNotes] add failed:', err);
      window.showToast?.('Could not save note' + (err?.code === 'permission-denied' ? ' — no access' : ''), 'error');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  // One-tap escalation: note → maintenance work order. Field shape mirrors
  // maintenance.js saveTask so the task renders/filters identically everywhere.
  // AUTHZ: the `tenants/{tid}/maintenance` create RULE is the gate — we never
  // trust "a note exists" (TASK-1141 mitigation 3).
  async function escalateNote(noteId) {
    if (!State.kind || !State.id) return;
    const note = State.notes.find(n => n.id === noteId);
    if (!note) return;
    const snippet = (note.body || '').slice(0, 120);
    const ok = window.showConfirm
      ? await window.showConfirm('Create Work Order', 'Open a maintenance work order from this note? "' + snippet + '"', { confirmText: 'Create Work Order' })
      : true;
    if (!ok) return;
    const uid = window.AppState?.user?.uid || null;
    if (!uid) { window.showToast?.('Sign in first', 'error'); return; }
    const authorName = window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown';
    const data = {
      assetType: State.kind,
      category: 'Repair',
      description: (note.body || '').slice(0, 2000),
      priority: 'normal',
      assignedTo: null,
      vendor: null,
      notes: '',
      status: 'open',
      source: 'equipment-note',
      sourceNoteId: noteId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      truckId: null, truckUnit: null,
      trailerId: null, trailerUnit: null,
      assetId: null, assetName: null,
    };
    if (State.kind === 'truck') { data.truckId = State.id; data.truckUnit = State.unitLabel || null; }
    else if (State.kind === 'trailer') { data.trailerId = State.id; data.trailerUnit = State.unitLabel || null; }
    else { data.assetId = State.id; data.assetName = State.unitLabel || null; }
    try {
      const woRef = await addDoc(collection(db, 'tenants', tenantId(), 'maintenance'), data);
      window.showToast?.('Work order created', 'success');
      // Append-only escalation marker: a follow-up thread note (update is
      // rules-denied, so the original note is never mutated).
      try {
        const marker = {
          body: 'Escalated to work order: "' + snippet + '"',
          createdAt: Timestamp.now(),
          authorUid: uid,
          authorName,
          workOrderId: woRef.id,
          escalatedFromNoteId: noteId,
        };
        const mRef = await addDoc(notesCol(State.kind, State.id), marker);
        State.notes.unshift({ id: mRef.id, ...marker });
        renderFeed();
      } catch (markErr) {
        // Non-fatal: the work order exists; the thread just lacks the marker.
        console.error('[EquipmentNotes] escalation marker failed:', markErr);
      }
    } catch (err) {
      console.error('[EquipmentNotes] escalation failed:', err);
      window.showToast?.(err?.code === 'permission-denied'
        ? 'You don’t have access to create work orders'
        : 'Could not create work order', 'error');
    }
  }

  async function deleteNote(noteId) {
    if (!State.kind || !State.id) return;
    const ok = window.showConfirm
      ? await window.showConfirm('Delete Note', 'Remove this note from the thread? This can’t be undone.', { confirmText: 'Delete' })
      : true;
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'tenants', tenantId(), KIND_COLLECTIONS[State.kind], State.id, 'notes', noteId));
      State.notes = State.notes.filter(n => n.id !== noteId);
      renderFeed();
    } catch (err) {
      console.error('[EquipmentNotes] delete failed:', err);
      window.showToast?.('Could not delete note', 'error');
    }
  }

  window.EquipmentNotes = {
    mount,
    unmount,
    addNote,
    escalateNote,
    deleteNote,
    canPost,
    canEscalate,
  };
})();
